import { useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Layers, Filter, Search, X, Heart, Zap, Shield, Wind, Eye, Compass, Star, Share2, Camera } from 'lucide-react';
import api from '../lib/api';
import { RARITY_UI } from '../lib/rarity';
import RarityBadge from '../components/RarityBadge';
import ShareCardModal from '../components/ShareCardModal';
import ArenaFrameTuner from '../components/ArenaFrameTuner';
import { useAuth } from '../contexts/AuthContext';
import { useT } from '../i18n/I18nContext';
const STAT_ICON = { health: Heart, attack: Zap, defense: Shield, speed: Wind, stealth: Eye, awareness: Eye, endurance: Compass, adaptability: Star };

export default function Vault() {
  const t = useT();
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rarityFilter, setRarityFilter] = useState('all');
  const [q, setQ] = useState('');
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    api.get('/cards').then(r => setCards(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const rarities = useMemo(() => {
    const s = new Set(cards.map(c => c.rarity));
    return ['all', ...['common','uncommon','rare','epic','legendary','mythic'].filter(r => s.has(r))];
  }, [cards]);

  const filtered = cards.filter(c =>
    (rarityFilter === 'all' || c.rarity === rarityFilter)
    && (!q || c.species_common_name.toLowerCase().includes(q.toLowerCase()))
  );

  return (
    <div className="space-y-5" data-testid="vault-root">
      <div>
        <p className="text-[10px] uppercase tracking-[0.25em] text-accent">{t('vault.title', 'Battle Vault')}</p>
        <h2 className="font-serif text-3xl">{t('vault.yourCards', 'Your cards')}</h2>
        <p className="text-xs text-muted-foreground mt-1">{t('vault.intro', 'Every observation mints one procedural Battle Card. Tap to view stats, abilities and origin story.')}</p>
      </div>

      <div className="grid grid-cols-3 gap-2">
        <Metric label={t('vault.total', 'Total')} value={cards.length} />
        <Metric label={t('vault.legendary', 'Legendary')} value={cards.filter(c => c.rarity === 'legendary').length} accent="#E9C46A" />
        <Metric label={t('vault.mythic', 'Mythic')} value={cards.filter(c => c.rarity === 'mythic').length} accent="#FF6EC7" />
      </div>

      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
          <input
            value={q}
            onChange={e => setQ(e.target.value)}
            placeholder={t('vault.searchPlaceholder', 'Search species…')}
            data-testid="vault-search"
            className="w-full bg-card border border-white/10 rounded-full pl-9 pr-4 py-2 text-sm outline-none focus:border-accent/60"
          />
        </div>
      </div>

      <div className="flex items-center gap-2 overflow-x-auto pb-1 -mx-1 px-1" data-testid="vault-filter-row">
        <Filter className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
        {rarities.map(r => (
          <button
            key={r}
            onClick={() => setRarityFilter(r)}
            data-testid={`vault-filter-${r}`}
            className={`flex-shrink-0 text-[11px] uppercase tracking-widest px-3 py-1.5 rounded-full border transition-colors ${
              rarityFilter === r ? 'border-accent/60 bg-accent/10 text-accent'
                : 'border-white/10 bg-card/50 text-muted-foreground hover:text-foreground'
            }`}
          >{r === 'all' ? t('vault.filterAll', 'all') : t(`vault.rarity.${r}`, r)}</button>
        ))}
      </div>

      {loading ? (
        <div className="grid grid-cols-2 gap-3">
          {[...Array(4)].map((_, i) => <div key={i} className="aspect-[3/4] rounded-2xl bg-card border border-white/5 animate-pulse" />)}
        </div>
      ) : filtered.length === 0 ? (
        <div className="rounded-3xl bg-card border border-white/5 p-8 text-center">
          <Layers className="w-8 h-8 text-muted-foreground mx-auto mb-3" strokeWidth={1.5} />
          <p className="font-serif text-lg">{t('vault.emptyTitle', 'Your vault is empty')}</p>
          <p className="text-xs text-muted-foreground mt-1">{t('vault.emptyBody', 'Discover any species to mint your first Battle Card.')}</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-3">
          {filtered.map((c) => (
            <CardTile key={c.card_id} card={c} onClick={() => setSelected(c)} t={t} />
          ))}
        </div>
      )}

      <AnimatePresence>
        {selected && <CardDetail card={selected} onClose={() => setSelected(null)} t={t} />}
      </AnimatePresence>
    </div>
  );
}

// -----------------------------------------------------------------------
// Helpers
// -----------------------------------------------------------------------
/**
 * Resolve the best display image URL for a battle card:
 *   - `image_url` is already a full URL (external or /api/files/...) — use as-is
 *   - `image_path` is a raw storage key — wrap in /api/files/ so the
 *     authenticated file endpoint serves it
 *   - `art_url` is the Nano Banana portrait — final fallback
 * Returns null when the card has no photo at all (e.g. legacy seed rows).
 */
function cardImageUrl(card) {
  const raw = card.image_url || card.image_path;
  if (raw) {
    if (raw.startsWith('http://') || raw.startsWith('https://') || raw.startsWith('/api/files/')) {
      return raw;
    }
    return `/api/files/${raw}`;
  }
  return card.art_url || null;
}

function CardTile({ card, onClick, t }) {
  const ui = RARITY_UI[card.rarity] || RARITY_UI.common;
  const img = cardImageUrl(card);
  return (
    <motion.button
      type="button"
      whileHover={{ y: -3 }}
      whileTap={{ scale: 0.97 }}
      onClick={onClick}
      data-testid="vault-card-tile"
      className={`group relative aspect-[3/4] rounded-2xl overflow-hidden ${ui.borderClass}`}
      style={ui.borderStyle}
    >
      <div className={`absolute inset-0 ${ui.cardClass}`} style={ui.cardStyle}>
        {img && (
          <img
            src={img}
            alt=""
            data-testid="vault-card-tile-photo"
            className="absolute inset-0 w-full h-full object-cover transition-transform duration-[7000ms] ease-out will-change-transform group-hover:scale-110 group-hover:translate-y-[-2%] group-hover:translate-x-[-1%]"
            loading="lazy"
          />
        )}
        {/* Scrim so text stays readable over any photo. */}
        <div className="absolute inset-0"
             style={{
               background: img
                 ? 'linear-gradient(180deg, rgba(4,10,8,0.55) 0%, rgba(4,10,8,0.15) 30%, rgba(4,10,8,0.5) 60%, rgba(4,10,8,0.9) 100%)'
                 : 'transparent',
             }}
        />
        <div className="relative p-3 flex flex-col justify-between h-full">
          <div className="flex items-center justify-between">
            <RarityBadge rarity={card.rarity} size="sm" showParticles={false} />
            <span className="text-[9px] font-mono text-white/70">{card.serial_label}</span>
          </div>
          <div>
            <p className="font-serif text-base leading-tight truncate">{card.species_common_name}</p>
            <p className="text-[10px] italic text-white/70 truncate">{card.species_scientific_name}</p>
            <div className="mt-2 flex items-center justify-between">
              <span className="text-[9px] uppercase tracking-widest text-white/70">{t ? t('vault.rating', 'Rating') : 'Rating'}</span>
              <span className="font-mono text-lg tabular-nums" style={{ color: ui.color }}>{card.battle_rating}</span>
            </div>
          </div>
        </div>
      </div>
    </motion.button>
  );
}

function CardDetail({ card: initialCard, onClose, t }) {
  const [card, setCard] = useState(initialCard);
  const ui = RARITY_UI[card.rarity] || RARITY_UI.common;
  const { user } = useAuth();
  const [sharing, setSharing] = useState(null);
  const [tuning, setTuning] = useState(false);
  const img = cardImageUrl(card);
  const ownsCard = user?.user_id && card.owner_id === user.user_id;
  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-md p-5 overflow-y-auto"
      onClick={onClose}
      data-testid="vault-card-detail"
    >
      <motion.div
        initial={{ y: 20, opacity: 0, scale: 0.95 }} animate={{ y: 0, opacity: 1, scale: 1 }} exit={{ y: 20, opacity: 0 }}
        transition={{ type: 'spring', stiffness: 300, damping: 24 }}
        onClick={e => e.stopPropagation()}
        className="max-w-md mx-auto"
      >
        <div className={`relative rounded-3xl ${ui.borderClass}`} style={ui.borderStyle}>
          <div className={`p-5 rounded-3xl ${ui.cardClass}`} style={ui.cardStyle}>
            <div className="flex items-start justify-between">
              <RarityBadge rarity={card.rarity} size="lg" />
              <button type="button" onClick={onClose} className="p-1.5 rounded-full bg-white/10 hover:bg-white/20"><X className="w-4 h-4" /></button>
            </div>
            <div className="mt-4">
              <p className="font-serif text-3xl leading-tight">{card.species_common_name}</p>
              <p className="text-xs italic text-muted-foreground">{card.species_scientific_name}</p>
              <p className="text-[10px] font-mono text-muted-foreground mt-1">DNA {card.dna_seed} · {card.serial_label} · Tier {card.tier}</p>
            </div>

            {img && (
              <div className="mt-4 relative rounded-2xl overflow-hidden aspect-[4/3]"
                   data-testid="detail-photo">
                <img src={img} alt="" className="absolute inset-0 w-full h-full object-cover" />
                <div className="absolute inset-0"
                     style={{ background: 'linear-gradient(180deg, rgba(4,10,8,0) 55%, rgba(4,10,8,0.7) 100%)' }} />
                <div className="absolute bottom-2 left-3 text-[9px] uppercase tracking-widest text-white/70">
                  {t('vault.explorerPhoto', "Explorer's photo")}
                </div>
              </div>
            )}

            <div className="mt-5 grid grid-cols-4 gap-1.5" data-testid="detail-stats">
              {Object.entries(card.stats || {}).map(([k, v]) => {
                const Icon = STAT_ICON[k] || Star;
                const short = k.slice(0, 3);
                return (
                  <div key={k} className="rounded-xl bg-black/30 border border-white/5 px-2 py-2 text-center">
                    <Icon className="w-3 h-3 mx-auto mb-0.5 text-muted-foreground" strokeWidth={2} />
                    <p className="text-[8px] uppercase tracking-widest text-muted-foreground">{t(`vault.stat.${short}`, short)}</p>
                    <p className="font-mono text-sm tabular-nums">{v}</p>
                  </div>
                );
              })}
            </div>

            <div className="mt-4 rounded-2xl bg-black/30 border border-white/5 px-3 py-2 flex items-center justify-between">
              <span className="text-[10px] uppercase tracking-widest text-muted-foreground">{t('vault.battleRating', 'Battle Rating')}</span>
              <span className="font-mono text-2xl tabular-nums" style={{ color: ui.color }}>{card.battle_rating}</span>
            </div>

            <div className="mt-4">
              <p className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2">{t('vault.abilities', 'Abilities')}</p>
              <div className="flex flex-wrap gap-1.5" data-testid="detail-abilities">
                {(card.abilities || []).map(a => <span key={a} className="text-xs px-2.5 py-1 rounded-full border border-white/15 bg-black/25">{a}</span>)}
              </div>
            </div>

            {card.personality?.length > 0 && (
              <div className="mt-3">
                <p className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2">{t('vault.personality', 'Personality')}</p>
                <p className="text-sm">{card.personality.join(' · ')}</p>
              </div>
            )}

            <div className="mt-5 rounded-2xl bg-black/30 border border-white/5 p-4">
              <p className="text-[10px] uppercase tracking-widest text-accent mb-2">{t('vault.originStory', 'Origin Story')}</p>
              <p className="text-sm italic leading-relaxed" data-testid="detail-origin-story">{card.origin_story}</p>
            </div>

            <div className="mt-5 grid grid-cols-3 gap-2 text-center">
              <MiniMeta label={t('vault.wins', 'Wins')} value={card.wins || 0} />
              <MiniMeta label={t('vault.losses', 'Losses')} value={card.losses || 0} />
              <MiniMeta label={t('vault.generation', 'Generation')} value={card.generation || 1} />
            </div>

            <p className="mt-4 text-[9px] leading-tight text-muted-foreground/70" data-testid="detail-disclaimer">
              {card.disclaimer}
            </p>

            <button
              data-testid="vault-share-card"
              onClick={() => setSharing(card)}
              className="mt-4 w-full py-3 rounded-full bg-accent text-black text-xs font-semibold uppercase tracking-widest inline-flex items-center justify-center gap-2"
            >
              <Share2 className="w-3.5 h-3.5" /> {t('vault.shareCard', 'Share this Battle Card')}
            </button>
            {ownsCard && img && (
              <button
                data-testid="vault-tune-framing"
                onClick={() => setTuning(true)}
                className="mt-2 w-full py-2.5 rounded-full bg-white/5 hover:bg-white/10 text-white/85 text-[11px] uppercase tracking-widest inline-flex items-center justify-center gap-2 border border-white/10"
              >
                <Camera className="w-3.5 h-3.5" /> {t('vault.tuneFraming', 'Tune arena framing')}
              </button>
            )}
          </div>
        </div>
      </motion.div>
      {sharing && (
        <ShareCardModal
          moment={{
            type: 'legendary',
            title: sharing.species_common_name,
            subtitle: sharing.species_scientific_name,
            species: {
              common_name: sharing.species_common_name,
              scientific_name: sharing.species_scientific_name,
              rarity: sharing.rarity,
              reference_photo_url: sharing.art_url || sharing.reference_photo_url,
            },
            score: sharing.battle_rating,
            score_label: 'BATTLE RATING',
            player: user ? { name: user.name, picture: user.picture } : null,
            date: sharing.created_at,
            share_url: `${window.location.origin}/d/${sharing.card_id}`,
          }}
          onClose={() => setSharing(null)}
        />
      )}
      {tuning && (
        <ArenaFrameTuner
          card={card}
          onClose={() => setTuning(false)}
          onSaved={frame => setCard(c => ({ ...c, frame }))}
        />
      )}
    </motion.div>
  );
}

function Metric({ label, value, accent }) {
  return (
    <div className="rounded-2xl bg-card border border-white/5 p-3 text-center">
      <p className="font-mono font-semibold text-2xl tabular-nums" style={{ color: accent }}>{value}</p>
      <p className="text-[9px] uppercase tracking-widest text-muted-foreground mt-1">{label}</p>
    </div>
  );
}

function MiniMeta({ label, value }) {
  return (
    <div className="rounded-xl bg-black/30 border border-white/5 py-2">
      <p className="font-mono text-base tabular-nums">{value}</p>
      <p className="text-[9px] uppercase tracking-widest text-muted-foreground">{label}</p>
    </div>
  );
}
